/**
 * Cuerpo de la vista previa lateral de propiedad (listado / contactos):
 * título + datos básicos; debajo solo secciones alineadas con la ficha (PropiedadForm),
 * visibles si tienen datos, plegadas por defecto.
 */
import { useState, useMemo, useEffect, useRef, useLayoutEffect } from "react";
import { videosPlayables, resolveVideoForPresentacion } from "../../presentacion/presentacionVideo";
import {
  formatPrecioVentaEntero,
  formatEuroAmount,
  parsePrecioVentaEntero,
  parseEuroAmount,
} from "../../utils/moneyParse";
import { formatCoordenadas, mapsUrlFromCoords } from "../../utils/buildDireccionPropiedad";
import { FichaResumenPropietariosContactos } from "./FichaResumenPropietariosContactos";
import {
  FichaResumenGeneralesDistribucionEquipamiento,
  hasGeneralesDistribucionEquipamiento,
} from "./FichaResumenGeneralesDistribucionEquipamiento";
import SeccionHistorial from "./SeccionHistorial";
import VisorImagenDocumento from "../captaciones/VisorImagenDocumento";
import BotonesVisorUbicacion from "./BotonesVisorUbicacion";

/**
 * Origen del backend para media relativa (`/api/...`, `/uploads/...`).
 * VITE_API_URL a veces es `https://host` y a veces `https://host/api` (base axios):
 * aquí siempre dejamos el host sin sufijo `/api` para no duplicar (p. ej. promo `/api/movil/...`).
 */
function resolveApiOrigin() {
  const raw =
    typeof import.meta !== "undefined" && import.meta.env?.VITE_API_URL
      ? String(import.meta.env.VITE_API_URL).trim().replace(/\/$/, "")
      : "";
  if (raw) return raw.replace(/\/api$/i, "") || raw;
  if (typeof window !== "undefined" && window.location?.hostname && window.location.hostname !== "localhost") {
    return window.location.origin;
  }
  return "http://localhost:4000";
}

export const API_ORIGIN = resolveApiOrigin();

export function hayDato(v) {
  if (v === true) return true;
  if (v === false || v === null || v === undefined) return false;
  if (typeof v === "number") return Number.isFinite(v);
  if (typeof v === "string") return v.trim() !== "";
  if (Array.isArray(v)) return v.length > 0;
  return Boolean(v);
}

function pick(d, p, key) {
  const a = d?.[key];
  const b = p?.[key];
  if (a !== undefined && a !== null && a !== "") return a;
  if (b !== undefined && b !== null && b !== "") return b;
  return null;
}

/** Población canónica: aux_poblacion vía poblacion_id (no texto legacy p.poblacion). */
function pickPoblacion(d, p) {
  return pick(d, p, "poblacion_nombre") || pick(d, p, "poblacion");
}

/** Importe entero en € (precio venta, etc.) */
function fmtPrecioEuro(v) {
  if (v == null || v === "") return null;
  const n = parsePrecioVentaEntero(v);
  if (!Number.isFinite(n)) return null;
  return `${formatPrecioVentaEntero(n)} €`;
}

/** Importe con céntimos (honorarios, IVA, gastos…) */
function fmtImporteEuro(v, sufijo = "") {
  if (v == null || v === "") return null;
  const n = parseEuroAmount(v);
  if (!Number.isFinite(n)) return null;
  return `${formatEuroAmount(n)} €${sufijo}`;
}

/** Fecha legible (dd/mm/aaaa) sin hora ISO */
function fmtFechaSimple(v) {
  if (v == null || v === "") return null;
  if (typeof v === "string") {
    const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  }
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" });
}

/** Contenedor con scroll (p. ej. panel vista previa) */
function getScrollParent(node) {
  let p = node?.parentElement;
  while (p) {
    const { overflowY } = getComputedStyle(p);
    if ((overflowY === "auto" || overflowY === "scroll") && p.scrollHeight > p.clientHeight + 2) return p;
    p = p.parentElement;
  }
  return null;
}

function SeccionColapsable({ titulo, children, theme }) {
  const [abierta, setAbierta] = useState(false);
  const rootRef = useRef(null);
  const t = theme?.colors || {};

  useLayoutEffect(() => {
    if (!abierta || !rootRef.current) return;
    const el = rootRef.current;
    const scrollParent = getScrollParent(el);
    const STICKY_TOP = 44;

    const scrollOnce = () => {
      el.style.scrollMarginTop = `${STICKY_TOP}px`;
      el.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollOnce();
        if (!scrollParent) return;
        window.setTimeout(() => {
          const pr = scrollParent.getBoundingClientRect();
          const er = el.getBoundingClientRect();
          const bottomClip = er.bottom - pr.bottom;
          const room = scrollParent.clientHeight - STICKY_TOP - 12;
          if (bottomClip <= 4) return;
          if (el.offsetHeight > room) return;
          scrollParent.scrollTo({
            top: scrollParent.scrollTop + bottomClip + 8,
            behavior: "smooth",
          });
        }, 280);
      });
    });
  }, [abierta]);

  return (
    <div
      ref={rootRef}
      className="rounded-lg mb-2"
      style={{ border: `1px solid ${t.border || "#3a4358"}` }}
    >
      <button
        type="button"
        onClick={() => setAbierta(!abierta)}
        className="w-full flex items-center justify-between px-3 py-2 text-sm font-semibold rounded-lg text-left"
        style={{ color: t.textSecondary || "#c8d2e0" }}
      >
        <span>{titulo}</span>
        <span style={{ color: t.textMuted || "#94a3b8" }}>{abierta ? "▲" : "▼"}</span>
      </button>
      {abierta && <div className="px-3 pb-3">{children}</div>}
    </div>
  );
}

export function CampoResumen({ label, valor, theme }) {
  if (!hayDato(valor) && valor !== 0) return null;
  const t = theme?.colors || {};
  return (
    <div className="py-1.5 px-2">
      <div className="text-[10px] uppercase tracking-wide mb-0.5 font-medium" style={{ color: t.textMuted || "#94a3b8" }}>
        {label}
      </div>
      <div className="text-sm font-semibold" style={{ color: t.textPrimary || "#f1f5f9" }}>
        {valor}
      </div>
    </div>
  );
}

function youtubeEmbedAutoplay(embedUrl) {
  if (!embedUrl || typeof embedUrl !== "string") return "";
  return embedUrl.includes("?") ? `${embedUrl}&autoplay=1` : `${embedUrl}?autoplay=1`;
}

export function FichaResumenGaleria({ fotos, videos = [], theme }) {
  const t = theme?.colors || {};
  const [fotoIdx, setFotoIdx] = useState(0);
  const [visorVideos, setVisorVideos] = useState(false);
  const [videoIdx, setVideoIdx] = useState(0);
  const visorFileVideoRef = useRef(null);

  const playables = useMemo(() => videosPlayables(Array.isArray(videos) ? videos : []), [videos]);

  const resolved = useMemo(
    () => (playables.length > 0 ? resolveVideoForPresentacion(playables[videoIdx]) : null),
    [playables, videoIdx]
  );

  useEffect(() => {
    if (!visorVideos || !resolved || resolved.kind !== "file") return;
    const el = visorFileVideoRef.current;
    if (!el) return;
    el.muted = false;
    const tid = window.setTimeout(() => {
      el.play().catch(() => {
        el.muted = true;
        el.play().catch(() => {});
      });
    }, 0);
    return () => window.clearTimeout(tid);
  }, [visorVideos, videoIdx, resolved?.src]);

  useEffect(() => {
    if (!visorVideos) return;
    const onKey = (e) => {
      if (e.key === "Escape") setVisorVideos(false);
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [visorVideos]);

  const hayFotos = Array.isArray(fotos) && fotos.length > 0;
  const hayVideos = playables.length > 0;

  if (!hayFotos && !hayVideos) return null;

  const raw = hayFotos
    ? typeof fotos[fotoIdx] === "string"
      ? fotos[fotoIdx]
      : fotos[fotoIdx]?.url
    : null;
  const url = raw
    ? raw.startsWith("http")
      ? raw
      : API_ORIGIN + (raw.startsWith("/") ? "" : "/") + raw
    : null;

  return (
    <div className="relative">
      {hayFotos && url ? (
        <img src={url} alt="" className="w-full h-96 object-cover" />
      ) : (
        <div
          className="w-full h-96 flex flex-col items-center justify-center gap-2 text-sm"
          style={{ background: t.border || "#1e293b", color: t.textMuted || "#94a3b8" }}
        >
          <span>Sin fotos en galería</span>
          {hayVideos && <span className="text-xs opacity-80">Pulsa el icono de vídeo para reproducir</span>}
        </div>
      )}

      <div className="absolute bottom-0 left-0 w-full overflow-hidden" style={{ height: "25px" }}>
        <svg viewBox="0 0 1440 40" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full" preserveAspectRatio="none">
          <path d="M0 40H1440V0C1440 0 1080 40 720 40C360 40 0 0 0 0V40Z" fill={t.bgCard} />
        </svg>
      </div>

      {hayFotos && (
        <div className="absolute top-2 right-2 bg-black/60 text-white text-xs rounded px-2 py-1">
          {fotoIdx + 1} / {fotos.length}
        </div>
      )}

      {hayVideos && (
        <div className="absolute top-2 left-2 z-[1]">
          <button
            type="button"
            title={playables.length > 1 ? `Ver ${playables.length} vídeos` : "Ver vídeo"}
            className="relative flex items-center justify-center w-10 h-10 rounded-full bg-black/65 hover:bg-black/80 text-white shadow-md border border-white/20"
            onClick={(e) => {
              e.stopPropagation();
              setVideoIdx(0);
              setVisorVideos(true);
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
              <path d="M4 6.5A2.5 2.5 0 0 1 6.5 4h7A2.5 2.5 0 0 1 16 6.5v11A2.5 2.5 0 0 1 13.5 20h-7A2.5 2.5 0 0 1 4 17.5v-11Zm15 1.2 3-1.73v12.06l-3-1.73V7.7Z" />
            </svg>
            {playables.length > 1 && (
              <span className="absolute -bottom-0.5 -right-0.5 min-w-[16px] h-4 px-0.5 rounded-full bg-blue-600 text-[10px] font-bold leading-4 text-center">
                {playables.length}
              </span>
            )}
          </button>
        </div>
      )}

      {hayFotos && fotos.length > 1 && (
        <>
          <button
            type="button"
            onClick={() => setFotoIdx((i) => (i > 0 ? i - 1 : fotos.length - 1))}
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full w-8 h-8 flex items-center justify-center text-gray-700 shadow"
          >
            ‹
          </button>
          <button
            type="button"
            onClick={() => setFotoIdx((i) => (i < fotos.length - 1 ? i + 1 : 0))}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full w-8 h-8 flex items-center justify-center text-gray-700 shadow"
          >
            ›
          </button>
        </>
      )}

      {visorVideos && hayVideos && resolved && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Vídeos de la propiedad"
          onClick={() => setVisorVideos(false)}
        >
          <div
            className="relative w-full max-w-4xl rounded-xl overflow-hidden bg-black shadow-2xl border border-white/10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-2 px-3 py-2 bg-neutral-900 text-white">
              <span className="text-sm font-medium truncate pr-2">
                {playables[videoIdx]?.titulo?.trim() || `Vídeo ${videoIdx + 1} de ${playables.length}`}
              </span>
              <button
                type="button"
                className="shrink-0 px-3 py-1 rounded-lg bg-white/10 hover:bg-white/20 text-sm font-semibold"
                onClick={() => setVisorVideos(false)}
              >
                Cerrar
              </button>
            </div>
            <div className="bg-black flex items-center justify-center min-h-[200px] max-h-[min(75vh,560px)]">
              {resolved.kind === "file" && (
                <video
                  ref={visorFileVideoRef}
                  key={resolved.src}
                  src={resolved.src}
                  controls
                  playsInline
                  autoPlay
                  className="w-full max-h-[min(75vh,560px)]"
                />
              )}
              {resolved.kind === "youtube" && (
                <iframe
                  key={youtubeEmbedAutoplay(resolved.embedUrl)}
                  title="YouTube"
                  src={youtubeEmbedAutoplay(resolved.embedUrl)}
                  className="w-full aspect-video max-h-[min(75vh,560px)]"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              )}
            </div>
            {playables.length > 1 && (
              <div className="flex items-center justify-between px-3 py-2 bg-neutral-900 text-white text-sm">
                <button
                  type="button"
                  className="px-3 py-1 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-40"
                  disabled={videoIdx <= 0}
                  onClick={() => setVideoIdx((i) => Math.max(0, i - 1))}
                >
                  ← Anterior
                </button>
                <span className="text-xs opacity-90">
                  {videoIdx + 1} / {playables.length}
                </span>
                <button
                  type="button"
                  className="px-3 py-1 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-40"
                  disabled={videoIdx >= playables.length - 1}
                  onClick={() => setVideoIdx((i) => Math.min(playables.length - 1, i + 1))}
                >
                  Siguiente →
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const REG_LABELS = [
  ["reg_registro_de", "Registro de"],
  ["reg_num_registro", "Nº registro"],
  ["reg_tomo", "Tomo"],
  ["reg_libro", "Libro"],
  ["reg_folio", "Folio"],
  ["reg_finca", "Finca"],
  ["reg_inscripcion", "Inscripción"],
  ["reg_ref_catastral", "Ref. catastral"],
  ["reg_utm", "UTM"],
  ["reg_idufir", "IDUFIR"],
  ["reg_valor_referencia", "Valor referencia"],
  ["reg_ano_construccion", "Año constr. (registro)"],
  ["reg_titulo", "Título"],
  ["reg_cargas", "Cargas"],
  ["reg_gastos", "Gastos"],
  ["reg_sup_catastro", "Sup. catastro"],
  ["reg_observaciones", "Observaciones"],
];

const NS_LABELS = [
  ["ns_registro", "Registro"],
  ["ns_tomo", "Tomo"],
  ["ns_libro", "Libro"],
  ["ns_folio", "Folio"],
  ["ns_finca", "Finca"],
  ["ns_inscripcion", "Inscripción"],
];

function hasLocalizacion(d, p) {
  return [
    pick(d, p, "direccion_completa"),
    pick(d, p, "direccion_extra"),
    pick(d, p, "nombre_via"),
    pick(d, p, "numero"),
    pick(d, p, "tipo_via_id"),
    pick(d, p, "parcela"),
    pick(d, p, "codigo_postal"),
    pick(d, p, "poblacion_nombre") || pick(d, p, "poblacion"),
    pick(d, p, "zona"),
    pick(d, p, "urbanizacion_id"),
    pick(d, p, "latitud"),
    pick(d, p, "longitud"),
  ].some(hayDato);
}

function hasEconomicos(d, p) {
  const hip = pick(d, p, "hipoteca");
  return [
    pick(d, p, "gastos_comunidad"),
    pick(d, p, "gastos_comunidad_obs"),
    pick(d, p, "basuras"),
    pick(d, p, "basuras_obs"),
    pick(d, p, "ibi"),
    pick(d, p, "ibi_obs"),
    pick(d, p, "valoracion"),
    pick(d, p, "valoracion_obs"),
    hip === true || hip === false,
    pick(d, p, "hipoteca_obs"),
  ].some(hayDato);
}

function hasWeb(d, p) {
  return [
    pick(d, p, "web_titulo"),
    pick(d, p, "web_desc_corta_es"),
    pick(d, p, "web_desc_larga_es"),
    pick(d, p, "descripcion_es"),
    d?.publicar_web === true,
  ].some(hayDato);
}

function hasPropietariosOContactos(d) {
  return (
    (Array.isArray(d?.propietarios) && d.propietarios.length > 0) ||
    (Array.isArray(d?.contactos_asociados) && d.contactos_asociados.length > 0)
  );
}

function hasInternos(d, p) {
  const tenemosLlaves = pick(d, p, "tenemos_llaves");
  const cal = pick(d, p, "calificacion");
  return [
    pick(d, p, "agente_nombre"),
    pick(d, p, "agente_id"),
    pick(d, p, "alias"),
    pick(d, p, "num_llave"),
    tenemosLlaves === true || tenemosLlaves === false,
    pick(d, p, "recoger_llaves_en"),
    pick(d, p, "llaves_ubicacion"),
    pick(d, p, "llaves_con_quien"),
    cal !== null && cal !== undefined && cal !== "",
    pick(d, p, "exclusiva"),
    pick(d, p, "exclusiva_duracion"),
    pick(d, p, "exclusiva_desde"),
    pick(d, p, "exclusiva_hasta"),
    pick(d, p, "info_visita"),
    pick(d, p, "observaciones_internas"),
    pick(d, p, "precio_negociable"),
    pick(d, p, "precio_negociable_obs"),
    pick(d, p, "precio_venta"),
    pick(d, p, "precio_propietario"),
    pick(d, p, "honorarios_porcentaje"),
    pick(d, p, "honorarios_euros"),
    pick(d, p, "iva_porcentaje"),
    pick(d, p, "iva_euros"),
    pick(d, p, "total_honorarios"),
  ].some(hayDato);
}

function hasRegistro(d) {
  return REG_LABELS.some(([k]) => hayDato(d?.[k]));
}

function hasNotaSimple(d) {
  return NS_LABELS.some(([k]) => hayDato(d?.[k]) || hayDato(d?.[`${k}_obs`]));
}

function hasDocumentacion(d) {
  return Array.isArray(d?.documentos) && d.documentos.length > 0;
}

function calificacionTexto(v) {
  if (v === null || v === undefined || v === "") return null;
  const labels = ["0★ Chollo", "1★ Adecuado", "2★ Caro", "3★ Fuera de mercado"];
  const i = Number(v);
  return Number.isFinite(i) && labels[i] ? labels[i] : String(v);
}

function IconoLupaDocumento({ className }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.35-4.35" />
    </svg>
  );
}

function absolutizarDocUrl(u) {
  if (!u || typeof u !== "string") return "";
  const t = u.trim();
  if (!t) return "";
  if (/^https?:\/\//i.test(t)) return t;
  return API_ORIGIN + (t.startsWith("/") ? "" : "/") + t;
}

/** Misma lógica que en SeccionDocumentacion.previewKind */
function docVistaPreviaKind(doc) {
  const mime = String(doc?.mime || "").toLowerCase();
  const name = String(doc?.nombre_documento || doc?.nombre_original || "").toLowerCase();
  if (mime.startsWith("image/")) return "image";
  if (mime === "application/pdf") return "pdf";
  if (name.endsWith(".pdf")) return "pdf";
  if (/\.(jpe?g|png|webp|gif)$/.test(name)) return "image";
  return "other";
}

export function FichaResumenVistaSecciones({ p, d, theme }) {
  const t = theme.colors;
  const [docModal, setDocModal] = useState(null);

  useEffect(() => {
    if (!docModal) return;
    const onKey = (e) => {
      if (e.key === "Escape") setDocModal(null);
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [docModal]);

  return (
    <>
    <div className="p-4">
      <h2 className="font-bold text-xl mb-1 text-center" style={{ color: t.textPrimary }}>
        {d.titulo || p.titulo || "Sin título"}
      </h2>
      <div className="flex flex-wrap items-center justify-center gap-3 text-sm mb-4" style={{ color: t.textSecondary }}>
        {(d.tipo || p.tipo) && <span>🏠 {d.tipo || p.tipo}</span>}
        {(pickPoblacion(d, p)) && <span>📍 {pickPoblacion(d, p)}</span>}
        {(d.precio_venta || p.precio_venta) && (
          <span>💰 {fmtPrecioEuro(d.precio_venta || p.precio_venta)}</span>
        )}
        {p.referencia && <span>✧ Ref: {p.referencia}</span>}
      </div>

      {hasGeneralesDistribucionEquipamiento(d, p) && (
        <SeccionColapsable theme={theme} titulo="📋 Datos Generales y Equipamiento">
          <FichaResumenGeneralesDistribucionEquipamiento p={p} d={d} theme={theme} />
        </SeccionColapsable>
      )}

      {hasLocalizacion(d, p) && (
        <SeccionColapsable theme={theme} titulo="📍 Localización (dirección)">
          <div className="grid grid-cols-2">
            <CampoResumen theme={theme} label="Dirección" valor={pick(d, p, "direccion_completa")} />
            <CampoResumen theme={theme} label="Planta / puerta" valor={pick(d, p, "direccion_extra")} />
            <CampoResumen theme={theme} label="Vía y número" valor={[pick(d, p, "nombre_via"), pick(d, p, "numero")].filter(Boolean).join(" ")} />
            <CampoResumen theme={theme} label="Código postal" valor={pick(d, p, "codigo_postal")} />
            <CampoResumen theme={theme} label="Población" valor={pickPoblacion(d, p)} />
            <CampoResumen theme={theme} label="Zona" valor={pick(d, p, "zona")} />
            {(() => {
              const lat = pick(d, p, "latitud");
              const lng = pick(d, p, "longitud");
              const txt = formatCoordenadas(lat, lng);
              const url = mapsUrlFromCoords(lat, lng);
              if (!txt || !url) return null;
              return (
                <div className="py-1.5 px-2">
                  <div className="text-[10px] uppercase tracking-wide mb-0.5 font-medium" style={{ color: theme?.colors?.textMuted || "#94a3b8" }}>
                    Coordenadas
                  </div>
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-semibold underline"
                    style={{ color: theme?.colors?.accentBlue || "#3b82f6" }}
                  >
                    {txt}
                  </a>
                </div>
              );
            })()}
          </div>
          <div className="mt-3 px-2">
            <BotonesVisorUbicacion
              propiedad={{
                ...p,
                ...d,
                poblacion: pickPoblacion(d, p),
                poblacion_nombre: pickPoblacion(d, p),
              }}
              fichaResumen
            />
          </div>
        </SeccionColapsable>
      )}

      {hasEconomicos(d, p) && (
        <SeccionColapsable theme={theme} titulo="💰 Económicos">
          <div className="grid grid-cols-2">
            <CampoResumen
              theme={theme}
              label="Gastos comunidad"
              valor={fmtImporteEuro(pick(d, p, "gastos_comunidad"), "/mes")}
            />
            <CampoResumen theme={theme} label="Obs. comunidad" valor={pick(d, p, "gastos_comunidad_obs")} />
            <CampoResumen
              theme={theme}
              label="Basuras"
              valor={fmtImporteEuro(pick(d, p, "basuras"), "/año")}
            />
            <CampoResumen theme={theme} label="Obs. basuras" valor={pick(d, p, "basuras_obs")} />
            <CampoResumen
              theme={theme}
              label="IBI"
              valor={fmtImporteEuro(pick(d, p, "ibi"), "/año")}
            />
            <CampoResumen theme={theme} label="Obs. IBI" valor={pick(d, p, "ibi_obs")} />
            <CampoResumen
              theme={theme}
              label="Valoración"
              valor={fmtImporteEuro(pick(d, p, "valoracion"))}
            />
            <CampoResumen theme={theme} label="Obs. valoración" valor={pick(d, p, "valoracion_obs")} />
            <CampoResumen
              theme={theme}
              label="Hipoteca"
              valor={
                pick(d, p, "hipoteca") === true
                  ? "Sí"
                  : pick(d, p, "hipoteca") === false
                    ? "No"
                    : null
              }
            />
            <CampoResumen theme={theme} label="Obs. hipoteca" valor={pick(d, p, "hipoteca_obs")} />
          </div>
        </SeccionColapsable>
      )}

      {hasWeb(d, p) && (
        <SeccionColapsable theme={theme} titulo="🌐 Web y descripciones">
          <div className="grid grid-cols-1 gap-1">
            <CampoResumen theme={theme} label="Publicar en web" valor={d.publicar_web ? "Sí" : null} />
            <CampoResumen theme={theme} label="Título web" valor={pick(d, p, "web_titulo")} />
            {pick(d, p, "web_desc_corta_es") && (
              <p className="text-xs leading-relaxed px-2 py-1" style={{ color: t.textMuted }}>
                <span className="font-semibold" style={{ color: t.textSecondary }}>
                  Desc. corta:{" "}
                </span>
                {String(pick(d, p, "web_desc_corta_es")).slice(0, 400)}
                {String(pick(d, p, "web_desc_corta_es")).length > 400 ? "…" : ""}
              </p>
            )}
            {pick(d, p, "web_desc_larga_es") && (
              <p className="text-xs leading-relaxed px-2 py-1" style={{ color: t.textMuted }}>
                {String(pick(d, p, "web_desc_larga_es")).slice(0, 500)}
                {String(pick(d, p, "web_desc_larga_es")).length > 500 ? "…" : ""}
              </p>
            )}
            {pick(d, p, "descripcion_es") && (
              <p className="text-xs leading-relaxed px-2 py-1" style={{ color: t.textMuted }}>
                <span className="font-semibold" style={{ color: t.textSecondary }}>
                  Descripción:{" "}
                </span>
                {String(pick(d, p, "descripcion_es")).slice(0, 400)}
                {String(pick(d, p, "descripcion_es")).length > 400 ? "…" : ""}
              </p>
            )}
          </div>
        </SeccionColapsable>
      )}

      {hasPropietariosOContactos(d) && (
        <SeccionColapsable theme={theme} titulo="👤 Propietarios y contactos asociados">
          <FichaResumenPropietariosContactos datos={d} theme={theme} />
        </SeccionColapsable>
      )}

      {hasInternos(d, p) && (
        <SeccionColapsable theme={theme} titulo="🔒 Internos">
          <div className="grid grid-cols-2">
            <CampoResumen theme={theme} label="Agente" valor={pick(d, p, "agente_nombre") || (pick(d, p, "agente_id") != null && pick(d, p, "agente_id") !== "" ? `ID ${pick(d, p, "agente_id")}` : null)} />
            <CampoResumen theme={theme} label="Alias" valor={pick(d, p, "alias")} />
            <CampoResumen theme={theme} label="Nº llave" valor={pick(d, p, "num_llave")} />
            <CampoResumen
              theme={theme}
              label="Tenemos llaves"
              valor={
                pick(d, p, "tenemos_llaves") === true
                  ? "Sí"
                  : pick(d, p, "tenemos_llaves") === false
                    ? "No"
                    : null
              }
            />
            <CampoResumen theme={theme} label="Recoger llaves en" valor={pick(d, p, "recoger_llaves_en")} />
            <CampoResumen
              theme={theme}
              label="Ubicación llaves"
              valor={
                pick(d, p, "llaves_ubicacion") === "pendiente_aceptacion"
                  ? `Pendiente aceptación${pick(d, p, "llaves_con_quien") ? `: ${pick(d, p, "llaves_con_quien")}` : ""}`
                  : pick(d, p, "llaves_ubicacion") === "externo"
                    ? `Fuera${pick(d, p, "llaves_con_quien") ? `: ${pick(d, p, "llaves_con_quien")}` : ""}`
                    : pick(d, p, "tenemos_llaves") === true
                      ? "En oficina"
                      : null
              }
            />
            <CampoResumen theme={theme} label="Calificación" valor={calificacionTexto(pick(d, p, "calificacion"))} />
            <CampoResumen theme={theme} label="Exclusiva" valor={pick(d, p, "exclusiva") ? "Sí" : pick(d, p, "exclusiva") === false ? "No" : null} />
            <CampoResumen theme={theme} label="Duración exclusiva" valor={pick(d, p, "exclusiva_duracion")} />
            <CampoResumen theme={theme} label="Exclusiva desde" valor={fmtFechaSimple(pick(d, p, "exclusiva_desde"))} />
            <CampoResumen theme={theme} label="Exclusiva hasta" valor={fmtFechaSimple(pick(d, p, "exclusiva_hasta"))} />
            <CampoResumen
              theme={theme}
              label="Precio negociable"
              valor={
                (() => {
                  const v = String(pick(d, p, "precio_negociable") || "").toLowerCase();
                  if (v === "si") return "Sí";
                  if (v === "no") return "No";
                  if (v === "no_sabe" || v === "ns") return "No sabe";
                  return pick(d, p, "precio_negociable") || null;
                })()
              }
            />
            <CampoResumen theme={theme} label="Obs. precio negociable" valor={pick(d, p, "precio_negociable_obs")} />
            <div className="col-span-2 mt-2 mb-1 pt-2" style={{ borderTop: `1px solid ${t.border}` }}>
              <div className="text-[10px] uppercase tracking-wide font-bold" style={{ color: t.accentGreen }}>
                Precios y honorarios
              </div>
            </div>
            <CampoResumen theme={theme} label="Precio venta" valor={fmtPrecioEuro(pick(d, p, "precio_venta"))} />
            <CampoResumen theme={theme} label="Precio propietario" valor={fmtPrecioEuro(pick(d, p, "precio_propietario"))} />
            <CampoResumen theme={theme} label="Honorarios %" valor={pick(d, p, "honorarios_porcentaje") != null && pick(d, p, "honorarios_porcentaje") !== "" ? `${pick(d, p, "honorarios_porcentaje")} %` : null} />
            <CampoResumen theme={theme} label="Honorarios €" valor={fmtImporteEuro(pick(d, p, "honorarios_euros"))} />
            <CampoResumen theme={theme} label="IVA %" valor={pick(d, p, "iva_porcentaje") != null && pick(d, p, "iva_porcentaje") !== "" ? `${pick(d, p, "iva_porcentaje")} %` : null} />
            <CampoResumen theme={theme} label="IVA €" valor={fmtImporteEuro(pick(d, p, "iva_euros"))} />
            <CampoResumen theme={theme} label="Total honorarios" valor={fmtImporteEuro(pick(d, p, "total_honorarios"))} />
          </div>
          {hayDato(pick(d, p, "info_visita")) && (
            <div className="mt-2 px-1">
              <div className="text-[10px] uppercase tracking-wide mb-1 font-medium" style={{ color: t.textMuted }}>
                Info visita
              </div>
              <p className="text-sm font-semibold whitespace-pre-wrap leading-relaxed" style={{ color: t.textPrimary }}>
                {String(pick(d, p, "info_visita")).trim()}
              </p>
            </div>
          )}
          {hayDato(pick(d, p, "observaciones_internas")) && (
            <div className="mt-2 px-1">
              <div className="text-[10px] uppercase tracking-wide mb-1 font-medium" style={{ color: t.textMuted }}>
                Observaciones internas
              </div>
              <p className="text-sm font-semibold whitespace-pre-wrap leading-relaxed" style={{ color: t.textPrimary }}>
                {String(pick(d, p, "observaciones_internas")).trim()}
              </p>
            </div>
          )}
        </SeccionColapsable>
      )}

      {hasRegistro(d) && (
        <SeccionColapsable theme={theme} titulo="📜 Registro">
          <div className="grid grid-cols-2">
            {REG_LABELS.map(([k, lab]) => (
              <CampoResumen key={k} theme={theme} label={lab} valor={d[k]} />
            ))}
          </div>
        </SeccionColapsable>
      )}

      {hasNotaSimple(d) && (
        <SeccionColapsable theme={theme} titulo="📄 Nota simple">
          <div className="grid grid-cols-2 sm:grid-cols-3">
            {NS_LABELS.map(([k, lab]) => {
              const m = d[k];
              const obs = d[`${k}_obs`];
              const partes = [];
              if (m != null && m !== "") partes.push(`${m} m²`);
              if (obs && String(obs).trim()) partes.push(String(obs).trim());
              const valor = partes.length ? partes.join(" · ") : null;
              return <CampoResumen key={k} theme={theme} label={lab} valor={valor} />;
            })}
          </div>
        </SeccionColapsable>
      )}

      {hasDocumentacion(d) && (
        <SeccionColapsable theme={theme} titulo="📎 Documentación">
          <ul className="text-sm space-y-1.5 list-none m-0 p-0" style={{ color: t.textSecondary }}>
            {(d.documentos || []).slice(0, 25).map((doc) => {
              const srcBase = absolutizarDocUrl(doc.url);
              const titulo = doc.nombre_documento || doc.nombre_original || "Documento";
              return (
                <li
                  key={`${doc.id}-${srcBase || ""}`}
                  className="flex items-center gap-2 py-1.5 px-2 rounded-lg"
                  style={{ background: `${t.border}22` }}
                >
                  <span className="truncate min-w-0 flex-1" title={titulo}>
                    {titulo}
                  </span>
                  <button
                    type="button"
                    disabled={!srcBase}
                    title={srcBase ? "Ver documento" : "Sin URL"}
                    aria-label={`Ver documento: ${titulo}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!srcBase) return;
                      const live = `${srcBase}${srcBase.includes("?") ? "&" : "?"}t=${Date.now()}`;
                      setDocModal({
                        title: titulo,
                        src: live,
                        kind: docVistaPreviaKind(doc),
                      });
                    }}
                    className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg border transition-colors disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90"
                    style={{ borderColor: t.border, color: t.accentBlue, background: t.bgCard }}
                  >
                    <IconoLupaDocumento />
                  </button>
                </li>
              );
            })}
            {(d.documentos || []).length > 25 && <li className="pl-2 text-xs opacity-80">… y más</li>}
          </ul>
        </SeccionColapsable>
      )}

      {p?.id && (
        <SeccionColapsable theme={theme} titulo="📜 Historial">
          <SeccionHistorial
            propiedadId={p.id}
            referencia={p.referencia || d?.referencia || ""}
            compacto
          />
        </SeccionColapsable>
      )}

    </div>

      {docModal && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 p-2 sm:p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Vista previa del documento"
          onClick={() => setDocModal(null)}
        >
          <div
            className="relative w-full max-w-[min(96vw,1200px)] h-[min(92vh,900px)] flex flex-col rounded-xl overflow-hidden shadow-2xl min-h-0"
            style={{ background: t.bgCard, border: `1px solid ${t.border}` }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="flex items-center justify-between gap-2 px-3 py-2.5 flex-shrink-0 border-b"
              style={{ borderColor: t.border, background: t.bgPanel || t.bgCard }}
            >
              <span className="text-sm font-semibold truncate pr-2" style={{ color: t.textPrimary }}>
                {docModal.title}
              </span>
              <button
                type="button"
                onClick={() => setDocModal(null)}
                className="shrink-0 px-3 py-1.5 rounded-lg text-sm font-semibold"
                style={{ background: t.border, color: t.textPrimary }}
              >
                Cerrar
              </button>
            </div>
            <div
              className="flex-1 min-h-0 overflow-hidden p-2 flex items-center justify-center"
              style={{ background: t.bgApp }}
            >
              {docModal.kind === "image" && (
                <div className="w-full h-full min-h-[70vh]">
                  <VisorImagenDocumento src={docModal.src} title={docModal.title} />
                </div>
              )}
              {docModal.kind === "pdf" && (
                <iframe
                  key={docModal.src}
                  title={docModal.title}
                  src={docModal.src}
                  className="w-full h-full min-h-[70vh] rounded border bg-white"
                  style={{ borderColor: t.border }}
                />
              )}
              {docModal.kind === "other" && (
                <div className="text-center px-4 py-8 max-w-md">
                  <p className="text-sm mb-4" style={{ color: t.textSecondary }}>
                    No hay vista previa integrada para este formato. Puedes abrirlo o descargarlo en una pestaña nueva.
                  </p>
                  <a
                    href={docModal.src}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block px-4 py-2.5 rounded-lg text-white text-sm font-semibold"
                    style={{ background: t.accentBlue }}
                  >
                    Abrir o descargar
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
